import type { ComponentProps, FC, RefObject } from 'react';
import React, { createRef } from 'react';
import { useFragment } from 'react-relay';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type {
	ContentBlogUnifiedQueryType,
	ContentUnifiedQueryType,
} from '@confluence/content-unified-query';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import { DeepLinkHandler } from '@confluence/deep-link-handler';
import { ContentOperationsFragment } from '@confluence/entity-operations/entry-points/ContentOperationsFragment.graphql.ts';
import type { ContentOperationsFragment$key } from '@confluence/entity-operations/entry-points/ContentOperationsFragmentKey.ts';
import { setPageContentElement } from '@confluence/object-comments/entry-points/pageContentDimensionsStore.ts';
import { HighlightActions } from '@confluence/highlight-actions/entry-points/HighlightActions.ts';
import { ScrollToHashLinkHandler } from '@confluence/scroll/entry-points/ScrollToHashLinkHandler.ts';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/onboarding-nth-user-setup-modal/entry-points/OnboardingNthUserContentTypeSpotlight.tsx';
import { expVal, UNSAFE_isExperimentEnabledNoExposure } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import { InlineActionsLoader } from './InlineActionsLoader/InlineActionsLoader.tsx';
import { getCanAddCommentsFromContentOperations } from './getCanAddCommentsFromContentOperations.ts';
import { useViewPageParams } from './useViewPageParams.ts';
import type {
	PageStatusQuery as PageStatusQueryType,
	PageStatusQueryVariables,
} from './graphql/__types__/PageStatusQuery.ts';
import type { PageStatusQueryRelaySpike_content$key } from './graphql/__generated__/PageStatusQueryRelaySpike_content.graphql.ts';
import {
	PageStatusQuery,
	PageStatusQueryRelaySpikeFragment,
} from './graphql/PageStatusQuery.graphql.ts';
// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const SPA = !process.env.REACT_SSR;

type PassThroughProps =
	| 'eventHandlerOverrides'
	| 'spaceKey'
	| 'error'
	| 'appearance'
	| 'hasInlineComments'
	| 'hasProgressiveRendering'
	| 'customPerfData';

type ViewPageContentRendererProps = Pick<
	ComponentProps<typeof PageContentRenderer>,
	PassThroughProps
> & {
	contentId: string;
	data?: ContentUnifiedQueryType | ContentBlogUnifiedQueryType;
	lastModifiedDate?: string;
	isArchived?: boolean;
	contentStatusError?: ApolloErrorInterop;
	isEmbeddedPage?: boolean;
	isSpaceOverview?: boolean;
	useRendererFragments?: boolean;
	preloadedCanAddComments?: boolean;
};

const styles = cssMap({
	containerWidth: {
		width: '100%',
		margin: '0 auto',
	},
	footerSpacing: {
		marginBottom: token('space.1000'),
	},
});

const ViewPageContentRenderer: FC<ViewPageContentRendererProps> = ({
	contentId,
	data,
	isArchived,
	isEmbeddedPage,
	isSpaceOverview,
	useRendererFragments = false,
	hasProgressiveRendering = true,
	...passThroughProps
}) => {
	const { versionOverride, embeddedContentRender, queryHash } = useViewPageParams();
	const rendererFragmentProps: Pick<
		ComponentProps<typeof PageContentRenderer>,
		'contentFragmentRef' | 'spaceFragmentRef'
	> = useRendererFragments
		? {
				contentFragmentRef: data?.content?.nodes?.[0] as ComponentProps<
					typeof PageContentRenderer
				>['contentFragmentRef'],
				spaceFragmentRef: data?.space as ComponentProps<
					typeof PageContentRenderer
				>['spaceFragmentRef'],
			}
		: {};
	const contentModeFragmentProps: Pick<
		ComponentProps<typeof PageContentRenderer>,
		'contentModeFragmentRef'
	> = Boolean(data?.content?.nodes?.[0]?.viewPageContentMode) &&
	fg('confluence_vp_mode_frag_reader')
		? {
				contentModeFragmentRef: data?.content?.nodes?.[0]?.viewPageContentMode as ComponentProps<
					typeof PageContentRenderer
				>['contentModeFragmentRef'],
			}
		: {};

	const refOfNodeToWatchForChange: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

	return (
		<div
			id="content"
			data-inline-comments-target="true"
			{...(UNSAFE_isExperimentEnabledNoExposure('cc-maui-experiment')
				? { 'data-remix-main-content': 'true' }
				: {})}
			data-testid="page-content-only"
			// Publishes this element to consumers that align to the content column (footer comments,
			// sticky draft bar, omnibar) so they do not have to find it in the DOM themselves.
			// No-exposure: registering the element has no user-visible effect on its own, so the
			// exposure is logged by `usePageContentDimensions` when a consumer actually reads it.
			ref={
				UNSAFE_isExperimentEnabledNoExposure('confluence_frontend_page_content_dimensions_store')
					? setPageContentElement
					: undefined
			}
			// The "page view" classes are included for compatibility with Connect add-ons, see navigator-context.js in the Confluence Backend repo
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="highlighter-context page view"
		>
			<Box
				xcss={cx(styles.containerWidth, !isSpaceOverview && styles.footerSpacing)}
				ref={refOfNodeToWatchForChange}
			>
				{SPA && refOfNodeToWatchForChange && queryHash ? (
					<DeepLinkHandler
						scrollTargetId={queryHash}
						refOfNodeToWatchForChange={refOfNodeToWatchForChange}
						scrollTarget="queryHash"
					/>
				) : null}

				{SPA && <ScrollToHashLinkHandler anchorContainer={refOfNodeToWatchForChange} />}
				<PageContentRenderer
					{...contentModeFragmentProps}
					{...{
						contentId,
						versionOverride,
						embeddedContentRender,
						queryHash,
						data,
						isArchived,
						isEmbeddedPage,
						isSpaceOverview,
						hasProgressiveRendering,
					}}
					{...rendererFragmentProps}
					{...passThroughProps}
				/>
				<OnboardingNthUserContentTypeSpotlight
					contentType={ContentType.PAGE}
					contentId={contentId}
				/>
			</Box>
		</div>
	);
};

export type ViewPageContentProps = ViewPageContentRendererProps & {
	hasInlineActions?: boolean;
	disableTableOverflowShadow?: boolean;
};

export type ViewPageContentWithStatusProps = Omit<
	ViewPageContentProps,
	'isArchived' | 'contentStatusError'
>;

const ViewPageContentBody: FC<ViewPageContentProps> = ({
	contentId,
	hasInlineComments,
	hasInlineActions = true,
	data,
	lastModifiedDate,
	isArchived,
	contentStatusError,
	preloadedCanAddComments,
	...passThroughProps
}) => {
	const isFabric = Boolean(
		data?.content?.nodes?.[0]?.body?.dynamic?.representation === 'atlas_doc_format',
	);
	const useRendererFragments =
		Boolean(data?.content?.nodes?.[0]) && fg('confluence_vp_body_frag_reader');

	const viewPageContentRenderer = (
		<>
			<ViewPageContentRenderer
				contentId={contentId}
				data={data}
				lastModifiedDate={lastModifiedDate}
				hasInlineComments={hasInlineComments}
				isArchived={isArchived}
				contentStatusError={contentStatusError}
				preloadedCanAddComments={preloadedCanAddComments}
				useRendererFragments={useRendererFragments}
				{...passThroughProps}
			/>
			{SPA && hasInlineActions && <InlineActionsLoader />}
		</>
	);

	// HighlightActions is only for TinyMCE
	if (isFabric) {
		return viewPageContentRenderer;
	}

	return (
		<HighlightActions
			contentId={contentId}
			contentType={ContentType.PAGE}
			hasInlineComments={hasInlineComments}
			lastModifiedDate={lastModifiedDate}
			isArchived={isArchived}
			contentStatusError={contentStatusError}
			isFabric={isFabric}
			preloadedCanAddComments={preloadedCanAddComments}
		>
			{viewPageContentRenderer}
		</HighlightActions>
	);
};

const ViewPageContentWithOperationsFragment: FC<
	ViewPageContentProps & { contentOperationsFragmentRef: ContentOperationsFragment$key }
> = ({ contentOperationsFragmentRef, ...props }) => {
	const contentOperations = useFragment(ContentOperationsFragment, contentOperationsFragmentRef);

	return (
		<ViewPageContentBody
			{...props}
			preloadedCanAddComments={getCanAddCommentsFromContentOperations(contentOperations)}
		/>
	);
};

export const ViewPageContent: FC<ViewPageContentProps> = (props) => {
	const contentOperationsFragmentRef = props.data?.content?.nodes?.[0];

	if (
		contentOperationsFragmentRef &&
		contentOperationsFragmentRef.id === props.contentId &&
		props.hasInlineComments !== false &&
		expVal('confluence_inline_comment_relay_content_ops', 'isEnabled', false)
	) {
		// The unified Relay operation composes ContentOperationsFragment above. The query node
		// also carries other fragment markers, so narrow it to the child fragment key here.
		const contentOperationsRef = contentOperationsFragmentRef as ComponentProps<
			typeof ViewPageContentWithOperationsFragment
		>['contentOperationsFragmentRef'];

		return (
			<ViewPageContentWithOperationsFragment
				{...props}
				contentOperationsFragmentRef={contentOperationsRef}
			/>
		);
	}

	return <ViewPageContentBody {...props} />;
};

const ViewPageContentWithFragmentStatus: FC<ViewPageContentWithStatusProps> = ({
	data,
	...props
}) => {
	const contentNode = data?.content?.nodes?.[0] ?? null;
	const statusData = useFragment(
		PageStatusQueryRelaySpikeFragment,
		contentNode as PageStatusQueryRelaySpike_content$key | null,
	);
	const isArchived = statusData?.status ? statusData.status === 'archived' : undefined;

	return <ViewPageContent {...props} data={data} isArchived={isArchived} />;
};

const ViewPageContentWithQueryStatus: FC<ViewPageContentWithStatusProps> = ({
	contentId,
	...props
}) => {
	const { data: contentStatusData, error: contentStatusError } = useQueryInterop<
		PageStatusQueryType,
		PageStatusQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-relay-graphql-operations -- Shared GraphQL operation, defined in ./graphql/PageStatusQuery.graphql.ts so the pact test and other consumers can share the same tagged node.
		PageStatusQuery,
		{
			variables: {
				contentId,
			},
		},
	);

	const isArchived = contentStatusData
		? String(contentStatusData?.content?.nodes?.[0]?.status) === 'archived'
		: undefined;

	return (
		<ViewPageContent
			{...props}
			contentId={contentId}
			isArchived={isArchived}
			contentStatusError={contentStatusError}
		/>
	);
};

export const ViewPageContentWithStatus: FC<ViewPageContentWithStatusProps> = (props) => {
	if (fg('confluence_frontend_view_page_relay_fragment_dedup')) {
		return <ViewPageContentWithFragmentStatus {...props} />;
	}

	return <ViewPageContentWithQueryStatus {...props} />;
};
